"use client";

import React, { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { UniversalEntityPage } from "@/components/shared/UniversalEntityPage";
import { useCommunicationsHooks } from "@/lib/hooks/api/communications";
import { useCommunicationTypes } from "@/lib/hooks/api/communications/communication-types";
import AutoImportModal from "@/components/shared/modals/AutoImportModal";
import { getEntityTableConfig } from "@/lib/entity-registry";

const COMMUNICATIONS_CONFIG = getEntityTableConfig<Communications>("communications");
import {
  GetContactssDocument,
  type Communications,
  type GetContactssQuery,
} from "@/src/gql/graphql";
import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import { buildCompanyScopedCommunicationsFilter } from "@/lib/domain/crm-entity-relationships";

interface DealCommunicationsProps {
  /** Deal UUID — used only for isolated column preferences per deal modal/page */
  dealId: string;
  /** Linked CRM company FK on the deal (canonical scope for communication rows). */
  companyId: string;
  companyDisplayName?: string;
  className?: string;
}

/**
 * Deal detail: communications for the opportunity's linked company —
 * **`company_id`-scoped OR `contact_id` in company roster**, same Hub rule as Company Activities / AI deal summary.
 */
export const DealCommunications: React.FC<DealCommunicationsProps> = ({
  dealId,
  companyId,
  companyDisplayName = "linked company",
  className = "",
}) => {
  const activityHooks = useCommunicationsHooks();
  const { options: typeOptions } = useCommunicationTypes();

  const { data: contactsData } = useQuery<GetContactssQuery>(GetContactssDocument, {
    variables: {
      first: 1000,
      filter: {
        company_id: { eq: companyId },
      },
    },
    skip: !companyId,
    errorPolicy: "all",
    fetchPolicy: "cache-first",
  });

  const companyContactIds = useMemo(
    () =>
      (contactsData?.contactsCollection?.edges ?? [])
        .map((edge) => edge?.node?.id)
        .filter((id): id is string => Boolean(id)),
    [contactsData]
  );

  const updatedConfig = useMemo(
    () =>
      ({
        ...COMMUNICATIONS_CONFIG,
        fields: {
          ...COMMUNICATIONS_CONFIG.fields,
          type_id: {
            ...COMMUNICATIONS_CONFIG.fields.type_id,
            options: typeOptions,
          },
        },
      }),
    [typeOptions]
  );

  const customComponents = useMemo(
    () => ({
      importModal: (props: { onSuccess: () => void; triggerButton: React.ReactNode }) => (
        <AutoImportModal config={updatedConfig} hooks={activityHooks} {...props} />
      ),
    }),
    [updatedConfig, activityHooks]
  );

  const companyScopedFilters = useMemo(
    () => buildCompanyScopedCommunicationsFilter(companyId, companyContactIds),
    [companyContactIds, companyId]
  );

  if (!companyId) {
    return (
      <p className="text-sm text-muted-foreground px-1">
        This deal has no linked company, so communications cannot be scoped here.
      </p>
    );
  }

  return (
    <div className={className}>
      <UniversalEntityPage
        entityName="communications"
        config={updatedConfig}
        hooks={activityHooks}
        filterPreset="all"
        customFilters={companyScopedFilters}
        title={`Communications — ${companyDisplayName}`}
        description="All communications tied to this deal's linked company (company-scoped rows and contacts under that account)."
        storageKey={`deal-detail-communications-${dealId}-${companyId}-columns`}
        customComponents={customComponents}
      />
    </div>
  );
};
